import { z } from "zod";
import type { StepCategory, StepLibraryModule } from "../types";
import { type SystemEvent } from "@erp/shared-types";
import { type SystemEventData } from "../events/system-event-schemas";
import type { Permission } from "@erp/authz";
import type { ExecutionLifecycle } from "../execution-lifecycle";
import type { WorkflowGuardrail, GuardrailContext } from "../utils/guardrails";
import type { VisibilityRule } from "./visibility";

// Re-export types needed by step implementations
export type { StepCategory, StepLibraryModule };

/**
 * Workflow execution context provided to steps during execution
 */
export interface StepExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> {
  workflowRunId: string;
  workflowDefinitionId?: string;
  stepRunId: string;
  organizationId: string;
  userId: string | null; // null for system-triggered workflows

  traceId: string;

  logger: Pick<Console, "log" | "info" | "warn" | "error" | "debug">;

  /**
   * Per-step lifecycle handle: cancellation state + signal, `cancel()` for
   * handler-initiated stops, `checkCancelled()` to bail at a safe point,
   * and `sendHeartbeat()` / `sendProgressHeartbeat(details)` for liveness
   * and progress observability. See `ExecutionLifecycle` for per-method
   * guidance.
   */
  lifecycle: ExecutionLifecycle;

  // Function to emit system events (typed based on step's emits configuration)
  // Events are validated against registered schemas and published to RabbitMQ
  emit: <E extends TEmits[number]>(
    eventType: E,
    data: SystemEventData[E],
  ) => Promise<void>;

  // Temporary compatibility field for schedule trigger migrations
  scheduledTime?: string;

  /**
   * Set to `true` when the step is being executed from the test-suite eval
   * runner. Handlers that have observable side-effects (DB writes, external
   * API calls that cost money or pollute production data) should branch on
   * this flag to skip those writes and return any necessary state inline on
   * the step output so the evaluator can read it without DB hydration.
   *
   * Defaults to undefined / falsy in production. Treat absence as "normal
   * execution" so existing handlers that don't know about this flag keep
   * working unchanged.
   */
  isEvalRun?: boolean;

  /**
   * Execution mode for this step.
   *
   * - `"run"` (default): normal first-time execution within a new workflow run.
   * - `"rerun"`: the step is being actively re-executed as part of a rerun.
   *   Handlers that cache or reuse prior outputs (e.g. document splits) should
   *   bypass the cache and redo the work.
   *
   * Kept as a string union (not boolean) so additional modes can be added
   * without breaking existing checks (e.g. `"force-rerun"`).
   *
   * Absent / undefined is equivalent to `"run"`.
   */
  runMode?: "run" | "rerun";

  /**
   * Raw step input payload provided by the workflow orchestrator before
   * schema validation/normalization. Useful for handlers that need broader
   * execution context than the typed input sockets expose.
   */
  workflowInputData?: Record<string, unknown>;

  /** Set when executing within a config-driven agent run. */
  agentRunId?: string;

  /** Guardrails loaded for this step (DB or inline config). Undefined if step has no guardrails. */
  guardrails?: WorkflowGuardrail[];
  /** Pre-built guardrail context for handler use in runGuardedOperation calls. */
  guardrailContext?: GuardrailContext;

  /**
   * Signal payload from the waitForSignal loop. Present on every iteration:
   * the initial run gets a bootstrap payload with signalName "__initial__",
   * subsequent runs get the actual signal data (e.g., triggeredByUserId).
   */
  signalPayload?: {
    signalName: string;
    payload?: Record<string, unknown>;
  };

  /**
   * User IDs watching the workflow or source entity that triggered this run.
   * Resolved at run start from CoreWatch. Steps can propagate these onto
   * newly created entities (e.g. tasks).
   */
  runWatchers?: string[];

  /**
   * ID of the task this agent run is currently executing, when spawned by the
   * orchestrated task loop. Task tools should prefer this over requiring the
   * LLM to supply a task ID explicitly.
   */
  currentTaskId?: string;

  /**
   * Yield to the workflow manager agent for assistance. Only present when
   * a manager agent is configured on the parent Workflow. Each call creates
   * a fresh AgentRun linked to the current WorkflowRun and StepRun.
   */
  yieldToManagerAgent?: (request: {
    reason: string;
    prompt: string;
    context?: Record<string, unknown>;
  }) => Promise<{
    agentRunId: string;
    outputData: Record<string, unknown>;
  } | null>;
}

export type ToolExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> = StepExecutionContext<TEmits>;

/** @deprecated Use StepExecutionContext */
export type WorkflowExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> = StepExecutionContext<TEmits>;

/**
 * Union of all registered step type identifiers. Add new entries here when
 * creating a new step — TypeScript will enforce that `StepConfig.type` and
 * `deprecated.replacedBy` are members of this union.
 */
export type StepTypeId =
  | "action_input"
  | "action_output"
  | "ai_reasoning"
  | "approval"
  | "archive_ship_and_debit_azure_files"
  | "await_oracle_order_numbers"
  | "background_agent"
  | "blob_storage_fetch"
  | "check_storage_write_errors"
  | "code_sandbox"
  | "close_remote_desktop"
  | "classify_document"
  | "classify_document_v2"
  | "classify_email"
  | "classify_master_data"
  | "classify_sd"
  | "compute_request_date"
  | "confidence_threshold"
  | "convert_e844sd_to_e844d"
  | "core_enum_fanout"
  | "create_ap_invoice_draft"
  | "create_jira_issue"
  | "create_pending_payment"
  | "create_pye_snapshot"
  | "create_sales_order_draft"
  | "create_draft_sales_order"
  | "create_sales_order_draft_from_document"
  | "create_task"
  | "csv_datasource_adapter"
  | "csv_datasource_writer"
  | "csv_transform_v4"
  | "csv_transform"
  | "data_asset"
  | "fwf_datasource_adapter"
  | "fwf_datasource_writer"
  | "decision"
  | "emit_system_event"
  | "detect_order_issues"
  | "compute_order_facts"
  | "resolve_order_fields"
  | "resolve_draft_details"
  | "reassign_draft_sales_order"
  | "classify_organization"
  | "submit_draft_sales_orders"
  | "email_rule_trigger"
  | "enqueue_rebate_session"
  | "entity_resolver"
  | "event_trigger"
  | "external_http_call"
  | "extract_ap_invoice_from_document"
  | "extract_ap_invoice_from_email"
  | "extract_document_content"
  | "extract_document_content_v2"
  | "extract_document_content_v3"
  | "extract_document_content_v4"
  | "extract_famous_order_from_email_subject"
  | "extract_remittance_from_document"
  | "fetch_purchase_order_from_famous"
  | "fetch_sales_order_from_famous"
  | "fetch_transfer_order_from_famous"
  | "filter_email"
  | "filter_email_attachments"
  | "filter_email_by_customer"
  | "fusion_sync"
  | "generate_task"
  | "hello_world"
  | "http_trigger"
  | "identify_and_filter_sd"
  | "identify_customer"
  | "identify_sd_customer"
  | "ingest_ar_data"
  | "ingest_ar_payments"
  | "ingest_bank_transactions_from_document"
  | "ingest_microsoft_graph_email"
  | "ingest_sales_load"
  | "interpolate_email_template"
  | "detect_language"
  | "segment_language"
  | "translate_language"
  | "login_to_remote_desktop"
  | "remote_desktop_execute"
  | "log_value"
  | "array_length"
  | "managed_mapper"
  | "master_data_bulk_insert"
  | "match_invoice_line_items"
  | "match_supplier_with_famous"
  | "observe_data_source_link"
  | "parse_file"
  | "parse_pipe_self_describing_file"
  | "pipeline_rows_insert"
  | "pipeline_rows_process"
  | "pipeline_rows_query"
  | "pipeline_rows_update"
  | "post_staged_transactions"
  | "investigate_849_rejections"
  | "match_849_email"
  | "prepare_collection_reminder"
  | "prepare_summary_email"
  | "process_edi_849"
  | "process_pick_ticket_bol"
  | "process_sd"
  | "query_stale_collection_threads"
  | "random_number_generator"
  | "reassign_task"
  | "rebate_csv_extract_constants"
  | "rebate_csv_map_fields"
  | "rebate_csv_preprocess"
  | "rebate_csv_transform"
  | "refresh_pending_bpids"
  | "render_document_as_text"
  | "render_order_approval_email"
  | "render_order_notification_email"
  | "resolve_approval_recipients"
  | "resolve_entity_watchers"
  | "resolve_inbound_email_for_staged_purchase_order"
  | "resolve_invoice_references"
  | "detect_short_pay_from_remittance"
  | "detect_deductions_from_remittance"
  | "parse_collection_filing_instruction"
  | "resolve_collection_filing_target"
  | "file_collection_evidence"
  | "send_collection_filing_receipt"
  | "match_collection_threads"
  | "run_rebate_forecast"
  | "run_replenishment_engine"
  | "run_demand_forecast"
  | "schedule_payment"
  | "schedule_trigger"
  | "send_announcement"
  | "send_announcement_v2"
  | "send_email"
  | "send_famous_invoice_to_orderful_810"
  | "send_famous_order_confirmation_to_orderful_855"
  | "send_famous_shipment_to_orderful_856"
  | "send_teams_notification"
  | "snooze_workflow_rerun"
  | "split_document_v2"
  | "split_documents"
  | "split_excel_sheets"
  | "split_excel_sheets_v4"
  | "split_staged_po"
  | "split_draft_sales_order"
  | "stateful_table_lookup_row"
  | "stateful_table_read_frame"
  | "stateful_table_write"
  | "submit_sales_orders"
  | "summarize_collection_threads"
  | "switch"
  | "sync_ap_invoice_to_famous"
  | "throw_error"
  | "transform_dmdtrans_sales_orders"
  | "transform_e844d_fields"
  | "transform_gsnetdef_policies"
  | "transform_initinfc_products"
  | "transform_initinfcs_products"
  | "transform_onhnlots_lots"
  | "transform_onhnunsh_snapshots"
  | "transform_skusupparm_supplier_products"
  | "transform_subchain_supercedes"
  | "transform_suptrans_purchase_orders"
  | "transform_vendinfc_warehouse_constraints"
  | "transform_vendmast_suppliers"
  | "voice_agent"
  | "wait_for_event"
  | "web_browser"
  | "watch_for_file_changes"
  | "workflow_action";

export type ToolTypeId =
  | "agent.ap_invoice_update.add_line_item"
  | "agent.ap_invoice_update.fetch_thread_invoices"
  | "agent.ap_invoice_update.load_email_attachments"
  | "agent.ap_invoice_update.load_email_thread"
  | "agent.ap_invoice_update.load_invoice"
  | "agent.ap_invoice_update.remove_line_item"
  | "agent.ap_invoice_update.update_invoice_header"
  | "agent.ap_invoice_update.update_line_item"
  | "agent.collection_email_draft.get_customer_payment_history"
  | "agent.collection_email_draft.get_related_threads"
  | "agent.collection_email_draft.load_thread_context"
  | "file_collection_evidence"
  | "lookup_collection_threads"
  | "agent.invoice_line_item_matching.arbitrate_matches"
  | "agent.invoice_line_item_matching.create_line_item_match"
  | "agent.invoice_line_item_matching.load_invoice_line_items"
  | "agent.invoice_line_item_matching.load_purchase_order"
  | "agent.invoice_line_item_matching.load_sales_order"
  | "agent.invoice_line_item_matching.load_shipment"
  | "agent.invoice_line_item_matching.load_supporting_documents"
  | "agent.invoice_line_item_matching.load_transfer_order"
  | "agent.invoice_line_item_matching.search_charges"
  | "agent.invoice_line_item_matching.search_po_line_items"
  | "agent.oms_email.create_email_draft"
  | "agent.oms_email.create_sales_order_draft"
  | "agent.oms_email.find_related_orders"
  | "agent.oms_email.find_validation_history"
  | "agent.oms_email.identify_sender"
  | "agent.oms_email.load_email"
  | "agent.oms_email.load_email_attachments"
  | "agent.oms_email.load_email_thread"
  | "agent.oms_email.load_organization_context"
  | "agent.oms_email.load_staged_order"
  | "agent.oms_email.search_customer_orders"
  | "agent.oms_email.validate_inventory"
  | "agent.oms_email.validate_pricing"
  | "agent.payment_review.load_context"
  | "agent.sd849_investigation.load_context"
  | "agent.sd849_investigation.investigate_transaction"
  | "agent.sd849_investigation.search_related"
  | "agent.sd849_email_matching.load_context"
  | "agent.sd849_email_matching.lookup_vdm"
  | "agent.sd849_email_matching.get_sent_lines"
  | "agent.sd849_email_matching.lookup_844_lines"
  | "agent.sd849_email_matching.list_supplier_submissions"
  | "agent.sd849_email_matching.search_submitted_lines"
  | "agent.remote_desktop.close"
  | "agent.remote_desktop.execute"
  | "agent.remote_desktop.login_to_remote_desktop"
  | "agent.session.generate_summary"
  | "agent.tasks.find_task_assignees"
  | "agent.tasks.get_next_task"
  | "agent.tasks.update_task_status"
  | "agent.web_browser.execute"
  | "list_entity_associations"
  | "list_external_system_references"
  | "shared.load_email_attachments"
  | "shared.load_email_thread";

export type ConfigTypeId = StepTypeId | ToolTypeId;

/**
 * Step configuration (metadata and schemas)
 */
export interface StepConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
> {
  type: ConfigTypeId;
  name: string;
  description: string;
  category: StepCategory;

  /**
   * Node library palette section (ERP module). When omitted, defaults to Platform.
   * Functional {@link category} is unchanged for badges and execution metadata.
   */
  libraryModule?: StepLibraryModule;

  // Zod schemas for validation
  inputSchema: TInput;
  outputSchema: TOutput;
  configSchema: TConfig;

  // Typed emissions - events this step can emit
  emits: TEmits;

  /**
   * Permissions required to execute this step/tool.
   *
   * Flat array with AND semantics.
   */
  requiredPermissions?: Permission[];

  // Temporal execution options
  execution?: {
    startToCloseTimeout?: string; // e.g., "5 minutes"
    scheduleToCloseTimeout?: string;
    // When set, Temporal reschedules the activity if no heartbeat is received
    // within this window — required to detect silent worker deaths (OOM,
    // SIGKILL during a deploy) in a bounded time instead of waiting for
    // startToCloseTimeout. Only meaningful for handlers that actually call
    // Context.current().heartbeat().
    heartbeatTimeout?: string;
    retry?: {
      maximumAttempts?: number;
      backoffCoefficient?: number;
      initialInterval?: string;
      maximumInterval?: string;
    };
    nonRetryableErrorTypes?: string[];
  };

  /**
   * Configure this step to wait for an external signal before executing.
   *
   * ⚠️ CAUTION: This is a powerful feature that should be used sparingly.
   * Steps with waitForSignal will pause the workflow until a signal arrives,
   * then execute repeatedly until `resolved: true` is returned.
   *
   * Requirements:
   * - Step handler MUST return `{ resolved: boolean, ... }` in its output
   * - Step should be idempotent (may execute multiple times per signal)
   * - Step should be lightweight (executes on every signal until resolved)
   */
  waitForSignal?: {
    /** Name of the signal to wait for */
    signalName: string;
    /** Timeout in milliseconds (default: 7 days) */
    timeoutMs?: number;
    /** Maximum iterations before failing (default: 50) */
    maxIterations?: number;
  };

  /**
   * Marks this step type as deprecated. Deprecated steps are hidden from the
   * node library palette and the AI agent catalog, but remain executable so
   * existing workflows keep working. The editor canvas shows a warning badge.
   */
  /**
   * Visibility predicates in disjunctive normal form (OR of AND groups).
   * When omitted the step is visible to everyone.
   * @see VisibilityRule
   */
  visibility?: VisibilityRule;

  deprecated?: {
    reason: string;
    replacedBy?: StepTypeId;
  };

  /**
   * Tool catalog keys related to this step type. Used by the workflow manager
   * agent to assemble a merged tool set that covers the workflow's capabilities.
   */
  toolTypes?: string[];

  /**
   * Evaluation configuration for this step type.
   *
   * Defines which output fields are relevant for human evaluation.
   * When seeding test cases from history, only these fields are shown
   * and stored as expected output. Other output fields (like confidence,
   * reasoning, etc.) are treated as implementation details.
   */
  evalConfig?: {
    /** Output field keys the evaluator should compare against. */
    evalOutputKeys: string[];
    /**
     * How the step structures its output.
     * - `"parallel_arrays"`: Output keys are parallel arrays (one entry per item).
     *   The eval UI unpacks them into per-item rows for labeling.
     * - `undefined` / `"object"`: Output is a flat object (default).
     */
    outputFormat?: "parallel_arrays" | "object";
    /**
     * When `outputFormat` is `"parallel_arrays"`, additional output keys
     * to display alongside eval keys for context (e.g., document name).
     * These are shown in the UI but not stored in expectedOutput.
     */
    contextOutputKeys?: string[];
  };
}

export type ToolConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
> = StepConfig<TConfig, TInput, TOutput, TEmits>;

/**
 * Step handler function
 */
export type StepHandler<
  /* eslint-disable @typescript-eslint/no-explicit-any */
  TStepConfig extends StepConfig<any, any, any, any> = StepConfig<
    any,
    any,
    any,
    any
  >,
  /* eslint-enable @typescript-eslint/no-explicit-any */
> = (
  input: z.infer<TStepConfig["inputSchema"]>,
  config: z.infer<TStepConfig["configSchema"]>,
  context: StepExecutionContext<TStepConfig["emits"]>,
) => Promise<z.infer<TStepConfig["outputSchema"]>>;

export type ToolHandler<
  /* eslint-disable @typescript-eslint/no-explicit-any */
  TToolConfig extends ToolConfig<any, any, any, any> = ToolConfig<
    any,
    any,
    any,
    any
  >,
  /* eslint-enable @typescript-eslint/no-explicit-any */
> = (
  input: z.infer<TToolConfig["inputSchema"]>,
  config: z.infer<TToolConfig["configSchema"]>,
  context: ToolExecutionContext<TToolConfig["emits"]>,
) => Promise<z.infer<TToolConfig["outputSchema"]>>;

/**
 * Complete step implementation (config + handler)
 * This defines how a step type is implemented in code.
 *
 * Note: This is different from StepDefinition in types/index.ts,
 * which defines a step instance in a workflow graph (UI representation).
 */
export interface WorkflowStep<
  TConfig extends z.ZodType = z.ZodType<Record<string, never>>,
  TInput extends z.ZodType = z.ZodType,
  TOutput extends z.ZodType = z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
  TStepConfig extends StepConfig<TConfig, TInput, TOutput, TEmits> = StepConfig<
    TConfig,
    TInput,
    TOutput,
    TEmits
  >,
> {
  config: TStepConfig;
  handler: StepHandler<TStepConfig>;
}

/**
 * Step registry for managing available workflow steps
 */
export class StepRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private steps = new Map<string, WorkflowStep<any, any, any, any>>();

  /**
   * Register a workflow step
   *
   * Can be called with either:
   * 1. A complete step (config + handler): register(step)
   * 2. A config and handler separately: register(stepConfig, handler)
   */
  public register<
    TConfig extends z.ZodType,
    TInput extends z.ZodType,
    TOutput extends z.ZodType,
    TEmits extends readonly SystemEvent[] = readonly [],
  >(
    config: StepConfig<TConfig, TInput, TOutput, TEmits>,
    handler: StepHandler<StepConfig<TConfig, TInput, TOutput, TEmits>>,
  ): void {
    const finalStep: WorkflowStep<TConfig, TInput, TOutput, TEmits> = {
      config,
      handler,
    };

    if (this.steps.has(finalStep.config.type)) {
      throw new Error(`Step type ${finalStep.config.type} already registered`);
    }

    this.steps.set(finalStep.config.type, finalStep);
  }

  /**
   * Get a step by type
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(type: string): WorkflowStep<any, any, any, any> {
    const step = this.steps.get(type);
    if (!step) {
      throw new Error(`Step type ${type} not registered`);
    }
    return step;
  }

  /**
   * Get step config only (without handler)
   * Useful for UI/metadata purposes
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getConfig(type: string): StepConfig<any, any, any, any> {
    return this.get(type).config;
  }

  /**
   * List all registered steps
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list(): WorkflowStep<any, any, any, any>[] {
    return Array.from(this.steps.values());
  }

  get size(): number {
    return this.steps.size;
  }

  /**
   * List steps by category
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listByCategory(category: StepCategory): WorkflowStep<any, any, any, any>[] {
    return this.list().filter((step) => step.config.category === category);
  }

  /**
   * Check if a step type is registered
   */
  has(type: string): boolean {
    return this.steps.has(type);
  }

  /**
   * Get all registered step types
   */
  getTypes(): string[] {
    return Array.from(this.steps.keys());
  }
}

/**
 * Global step registry instance
 */
export const stepRegistry = new StepRegistry();

// Export helpers
export * from "./helpers";
export * from "./visibility";
